import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const NewsCard = ({ article, onBookmark, onShare, onComment }) => {
  const [isBookmarked, setIsBookmarked] = useState(article?.isBookmarked || false);
  const [showComments, setShowComments] = useState(false);

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    onBookmark?.(article?.id, !isBookmarked);
  };

  const handleShare = () => {
    onShare?.(article);
  };

  const handleComment = () => {
    setShowComments(!showComments);
    onComment?.(article?.id);
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const articleTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - articleTime) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <article className="bg-card border border-border rounded-lg overflow-hidden premium-hover animate-fade-in">
      {/* Article Image */}
      {article?.image && (
        <div className="relative h-48 overflow-hidden">
          <Image
            src={article?.image}
            alt={article?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-3 left-3">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              article?.category === 'Market Analysis' ? 'bg-success/20 text-success' :
              article?.category === 'Authentication' ? 'bg-primary/20 text-primary' :
              article?.category === 'Community'? 'bg-conversion-accent/20 text-conversion-accent' : 'bg-muted/80 text-muted-foreground'
            }`}>
              {article?.category}
            </span>
          </div>
          {article?.isPremium && (
            <div className="absolute top-3 right-3">
              <div className="bg-primary/90 text-primary-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                <Icon name="Crown" size={12} />
                <span>Premium</span>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Article Content */}
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full overflow-hidden">
              <Image
                src={article?.author?.avatar}
                alt={article?.author?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-card-foreground">{article?.author?.name}</p>
              <p className="text-xs text-muted-foreground">{formatTimeAgo(article?.publishedAt)}</p>
            </div>
          </div>
          {article?.trending && (
            <div className="flex items-center space-x-1 text-conversion-accent">
              <Icon name="TrendingUp" size={14} />
              <span className="text-xs font-medium">Trending</span>
            </div>
          )}
        </div>

        {/* Title and Excerpt */}
        <h3 className="text-lg font-semibold text-card-foreground mb-2 line-clamp-2">
          {article?.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {article?.excerpt}
        </p>

        {/* Tags */}
        {article?.tags && article?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {article?.tags?.slice(0, 3)?.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-muted/50 text-muted-foreground text-xs rounded-md"
              >
                #{tag}
              </span>
            ))}
            {article?.tags?.length > 3 && (
              <span className="text-xs text-muted-foreground">+{article?.tags?.length - 3} more</span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleComment}
              className="flex items-center space-x-2 text-muted-foreground hover:text-card-foreground transition-colors duration-200"
            >
              <Icon name="MessageCircle" size={16} />
              <span className="text-sm">{article?.commentCount || 0}</span>
            </button>
            
            <button
              onClick={handleShare}
              className="flex items-center space-x-2 text-muted-foreground hover:text-card-foreground transition-colors duration-200"
            >
              <Icon name="Share2" size={16} />
              <span className="text-sm">Share</span>
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleBookmark}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                isBookmarked
                  ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-card-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={isBookmarked ? "Bookmark" : "BookmarkPlus"} size={16} />
            </button>
            
            <Button variant="outline" size="sm">
              Read More
            </Button>
          </div>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4 pt-4 border-t border-border animate-slide-up">
            <div className="space-y-3">
              {article?.recentComments?.slice(0, 2)?.map((comment, index) => (
                <div key={index} className="flex space-x-3">
                  <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                    <Image
                      src={comment?.author?.avatar}
                      alt={comment?.author?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-card-foreground">{comment?.author?.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{comment?.content}</p>
                  </div>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="w-full">
                View All Comments
              </Button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default NewsCard;